import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import { describeResult, isSuccess } from '../lib/packingListRequest';
import { FILE_KIND, fileKindOf, msUntilFileUrlExpiry, openDownloadUrl } from '../lib/packingListFile';

/** 엑셀의 화면용 PDF가 생기기를 기다리는 간격과 횟수. 보통 몇 초 안에 생기고 1분이 지나면 포기한다(격자로 남는다). */
export const PREVIEW_POLL_INTERVAL_MS = 2000;
export const PREVIEW_POLL_MAX = 30;

const isSpreadsheet = (file) => {
    const kind = fileKindOf(file?.mime_type, file?.original_filename);
    return kind === FILE_KIND.SHEET || kind === FILE_KIND.LEGACY_SHEET;
};

/**
 * 원본 뷰어가 쓰는 단기 URL(read/file_url, inline).
 * - status: idle(아직) · loading · ready(file 있음) · none(원본 없는 문서) · error
 * - file.preview_url: 엑셀 원본의 화면용 PDF. 원본 url과 같이 만료되고, 없으면 null.
 * - expired: 서버가 준 expires_at이 지났다. 이미 그려진 원본은 그대로 두고, 다음에 URL이 필요할 때(다시 그리기·Reload) 재발급한다.
 *   만료마다 자동으로 바꿔 끼우지 않는 것은, PDF 프레임의 src가 바뀌면 보고 있던 페이지가 처음으로 돌아가기 때문이다.
 * - waitForPreview: 파싱 대기 화면처럼 PDF가 곧 생길 때 켠다. PDF가 없는 동안만 다시 묻고 생겼을 때만 바꿔 끼운다.
 *   묻는 동안은 previewPending으로 준비 중 안내를 보여주고, 포기하면 격자로 떨어진다.
 * 수기 문서(hasFile=false)나 아직 서버에 없는 문서(docId 없음)는 요청하지 않는다.
 */
export default function usePackingListFileUrl(commands, { docId, hasFile, waitForPreview = false, previewPollMs = PREVIEW_POLL_INTERVAL_MS }) {
    const [state, setState] = useState({ status: 'idle', file: null, error: null, expired: false });
    const requestSeq = useRef(0);
    const pollCount = useRef(0);
    const [pollTick, setPollTick] = useState(0);

    const load = useCallback(async () => {
        const seq = requestSeq.current + 1;
        requestSeq.current = seq;
        pollCount.current = 0;
        if (docId == null || !hasFile) {
            setState({ status: 'none', file: null, error: null, expired: false });
            return null;
        }
        setState((s) => ({ ...s, status: 'loading', error: null }));
        const result = await commands.readFileUrl({ id: docId, disposition: 'inline' });
        // 다른 문서로 바뀐 뒤 도착한 옛 응답은 버린다.
        if (seq !== requestSeq.current) return result;
        if (!isSuccess(result)) setState({ status: 'error', file: null, error: describeResult(result), expired: false });
        else if (!result.data) setState({ status: 'none', file: null, error: null, expired: false });
        else setState({ status: 'ready', file: result.data, error: null, expired: false });
        return result;
    }, [commands, docId, hasFile]);

    useEffect(() => {
        load();
    }, [load]);

    /** 만료 시각이 되면 expired만 켠다. 재발급은 화면이 필요할 때 refresh로 한다. */
    useEffect(() => {
        if (state.status !== 'ready' || !state.file?.expires_at) return undefined;
        const timer = setTimeout(() => setState((s) => (s.file === state.file ? { ...s, expired: true } : s)), msUntilFileUrlExpiry(state.file.expires_at));
        return () => clearTimeout(timer);
    }, [state.status, state.file]);

    /** 엑셀 원본의 PDF가 아직 없으면 잠시 뒤 다시 묻는다. 그 사이 Reload·문서 전환이 끼어들면 그 응답은 버린다. */
    useEffect(() => {
        if (!waitForPreview || state.status !== 'ready' || !state.file) return undefined;
        if (state.file.preview_url || !isSpreadsheet(state.file)) return undefined;
        if (pollCount.current >= PREVIEW_POLL_MAX) return undefined;
        const seq = requestSeq.current;
        const timer = setTimeout(async () => {
            pollCount.current += 1;
            const result = await commands.readFileUrl({ id: docId, disposition: 'inline' });
            if (seq !== requestSeq.current) return;
            if (isSuccess(result) && result.data?.preview_url) setState({ status: 'ready', file: result.data, error: null, expired: false });
            else setPollTick((t) => t + 1);
        }, previewPollMs);
        return () => clearTimeout(timer);
    }, [waitForPreview, previewPollMs, state.status, state.file, pollTick, commands, docId]);

    const previewPending = waitForPreview && state.status === 'ready' && Boolean(state.file) && !state.file.preview_url && isSpreadsheet(state.file) && pollCount.current < PREVIEW_POLL_MAX;
    return { ...state, previewPending, refresh: load };
}

/**
 * 원본 다운로드. 누를 때마다 attachment URL을 새로 받는다 — 화면에 둔 URL은 10분이면 만료되고, 재발급 때 서버가 권한을 다시 확인한다.
 * 저장 파일명은 서버가 Content-Disposition에 실은 il_file.original_filename이다.
 */
export function useOriginalDownload(commands) {
    const dispatch = useDispatch();
    const [downloading, setDownloading] = useState(false);
    const download = useCallback(
        async (docId) => {
            if (docId == null || downloading) return null;
            setDownloading(true);
            try {
                const result = await commands.readFileUrl({ id: docId, disposition: 'attachment' });
                if (!isSuccess(result)) return result;
                if (!result.data?.url) {
                    dispatch(setSnackbar({ open: true, severity: 'info', message: 'This document has no original file.' }));
                    return result;
                }
                openDownloadUrl(result.data.url);
                return result;
            } finally {
                setDownloading(false);
            }
        },
        [commands, dispatch, downloading]
    );
    return { download, downloading };
}
